<template>
  <div class="flex flex-col gap-y-6 py-4">
    <div
      data-testid="upload-modal-arquivo-input"
      class="relative border-2 border-dashed border-primary30 rounded-[24px] bg-primary5/30 p-10 flex flex-col items-center justify-center transition-all cursor-pointer hover:opacity-80"
      @click="triggerFileInput"
      @dragover.prevent
      @drop.prevent="handleFileDrop"
    >
      <input
        type="file"
        ref="fileInput"
        class="hidden"
        accept=".jpeg,.jpg,.png,.pdf"
        @change="handleFileChange"
      />
      <div class="text-primary30 mb-4">
        <v-icon :icon="mdiTrayArrowUp" size="64" />
      </div>
      <p class="text-[16px] text-textPrimary text-center">
        Arraste o arquivo ou
        <span class="text-[#0043CE] underline">clique aqui</span> para selecionar
      </p>
      <div class="mt-4 text-center text-[14px] text-primary30">
        <p>Formatos aceitos: .jpeg, .jpg, .png e .pdf</p>
        <p>O arquivo não pode ser maior que 50mb</p>
      </div>
    </div>

    <div v-if="modelValue" class="flex items-center justify-between bg-white">
      <div class="flex items-center gap-x-3">
        <div class="text-neutralLight">
          <v-icon :icon="mdiFileOutline" size="48" />
        </div>
        <div class="flex flex-col">
          <span class="text-sm font-medium text-textPrimary truncate max-w-[250px]">
            {{ modelValue.name }}
          </span>
          <span class="text-xs text-left text-neutralLight">
            {{ formatSize(modelValue.size) }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-x-2">
        <button
          data-testid="upload-modal-download-button"
          @click="downloadFile"
          class="p-2 text-primary30 hover:bg-negative/10 rounded-full transition-colors"
        >
          <v-icon :icon="mdiTrayArrowDown" size="24" />
        </button>
        <button
          data-testid="upload-modal-remove-file-button"
          @click="removeFile"
          class="p-2 text-primary30 hover:bg-negative/10 rounded-full transition-colors"
        >
          <v-icon :icon="mdiCloseCircleOutline" size="24" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { mdiTrayArrowUp, mdiFileOutline, mdiTrayArrowDown, mdiCloseCircleOutline } from '@mdi/js'
import { useToast } from 'vue-toastification'

const props = defineProps<{
  modelValue: File | null
}>()

const emit = defineEmits(['update:modelValue'])

const fileInput = ref<HTMLInputElement | null>(null)
const toast = useToast()

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files?.length) processFile(target.files[0])
}

const handleFileDrop = (e: DragEvent) => {
  if (e.dataTransfer?.files.length) processFile(e.dataTransfer.files[0])
}

const processFile = async (file: File) => {
  if (file.size > MAX_FILE_SIZE) {
    toast.error('O arquivo excede o limite de 50MB.')
    if (fileInput.value) fileInput.value.value = ''
    return
  }

  // Materializa o conteudo do arquivo em memoria no momento da selecao.
  // Em navegadores mobile, o File original e uma referencia "lazy" (ex: arquivos
  // vindos de apps como CamScanner/Files) que pode ficar inacessivel na hora do
  // upload, fazendo o POST falhar antes de chegar ao backend. Ler o buffer aqui
  // garante que o conteudo estara disponivel no submit. NAO remover (regressao
  // conhecida: adicionado em #1430, removido por engano em #1751).
  try {
    const buffer = await file.arrayBuffer()
    const inMemoryFile = new File([buffer], file.name, {
      type: file.type,
      lastModified: file.lastModified
    })
    emit('update:modelValue', inMemoryFile)
  } catch (err) {
    console.error('[file-select] failed to buffer file:', err)
    toast.error('Não foi possível carregar o arquivo. Tente novamente.')
    if (fileInput.value) fileInput.value.value = ''
  }
}

const removeFile = () => {
  emit('update:modelValue', null)
  if (fileInput.value) fileInput.value.value = ''
}

const downloadFile = () => {
  if (!props.modelValue) return

  const url = URL.createObjectURL(props.modelValue)
  const link = document.createElement('a')
  link.href = url
  link.download = props.modelValue.name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'kb', 'mb', 'gb']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + sizes[i]
}
</script>
